import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { accountsAPI } from '@/services/api'
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'

const AccountsDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAccounts()
  }, [])

  const fetchAccounts = async () => {
    setLoading(true)
    try {
      const data = await accountsAPI.list()
      setAccounts(data)
    } catch (err) {
      console.error('Erro ao carregar contas:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Calculate statistics
  const totalBalance = accounts.reduce((sum, acc) => sum + parseFloat(acc.balance || 0), 0)
  const positiveAccounts = accounts.filter(acc => parseFloat(acc.balance || 0) > 0)
  const negativeAccounts = accounts.filter(acc => parseFloat(acc.balance || 0) < 0)
  const totalPositive = positiveAccounts.reduce((sum, acc) => sum + parseFloat(acc.balance || 0), 0)
  const totalNegative = Math.abs(negativeAccounts.reduce((sum, acc) => sum + parseFloat(acc.balance || 0), 0))

  // Prepare chart data
  const chartData = accounts
    .map(acc => ({
      name: acc.name,
      value: Math.abs(parseFloat(acc.balance || 0))
    }))
    .filter(item => item.value > 0)

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard de Contas</h1>
        <button
          onClick={() => navigate('/accounts')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Ver Todas as Contas
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Saldo Total</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totalBalance)}</p>
            </div>
            <BanknotesIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Contas Positivas</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totalPositive)}</p>
              <p className="text-green-100 text-xs mt-1">{positiveAccounts.length} contas</p>
            </div>
            <ArrowTrendingUpIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Contas Negativas</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totalNegative)}</p>
              <p className="text-red-100 text-xs mt-1">{negativeAccounts.length} contas</p>
            </div>
            <ArrowTrendingDownIcon className="w-8 h-8 text-red-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total de Contas</p>
              <p className="text-2xl font-bold mt-1">{accounts.length}</p>
            </div>
            <ChartBarIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Distribution Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição de Saldos</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-gray-500 py-12">Nenhum dado para exibir</p>
        )}
      </div>

      {/* Top Accounts */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Principais Contas</h3>
        <div className="space-y-3">
          {accounts
            .sort((a, b) => Math.abs(parseFloat(b.balance || 0)) - Math.abs(parseFloat(a.balance || 0)))
            .slice(0, 5)
            .map((account) => {
              const balance = parseFloat(account.balance || 0)
              return (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/accounts/${account.id}/statement`)}
                >
                  <div>
                    <p className="font-medium text-gray-900">{account.name}</p>
                    <p className="text-sm text-gray-500">{account.bank}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-semibold ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(balance)}
                    </p>
                    <p className="text-xs text-gray-500">{account.type}</p>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default AccountsDashboard
